import { ApplicationRef, Inject, Injectable, isDevMode, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, ReplaySubject, Subscription } from 'rxjs';
import { shareReplay, startWith } from 'rxjs/operators';
import { NotificationModel } from 'src/app/notification/models/notification';
import { NOTIFICATION } from 'src/app/notification/tokens';
import { MapToGA4 } from 'src/business/Mapper/map-to-ga4-item';
import { PromiseUtils } from 'src/business/promise.util';
import { AddLineItemModel, BasketClient, BasketResponseModel, BasketReturnModel, DeliveryAddressReturnModel, LineItemReturnModel, LinesClient, NewsletterClient, NewsletterPostBody, OrderClient, Origin, PaymentMethod as PaymentMethodModel, PickupPoint, ShippingMethodReturnModel, UpdateBasketAddressModel, UpdateShippingModel } from 'src/business/services/client.generated';
import { countriesMock, ICountry } from 'src/commerce/countries.mock';
import { BASE_URL, CATALOG } from 'src/core-lib/misselaineous/boot.tokens';
import { GOOGLE_MAPS_API_KEY } from 'src/externals/google-maps/tokens';
import { GA4 } from 'src/externals/tag-manager/models/ga4.model';
import { TagManagerService } from 'src/externals/tag-manager/services/tag-manager.service';
import { UrlUtil } from 'src/utils/url.util';


export interface PaymentMethod extends Partial<PaymentMethodModel> { name?: string; image?: string; };
export type PaymentMethods = Array<PaymentMethod>;

@Injectable({
    providedIn: 'root'
})
export class BasketService implements OnDestroy {
    private _basket$: BehaviorSubject<BasketReturnModel>;
    private _shippingMethods$: BehaviorSubject<ShippingMethodReturnModel[]>;
    private _basketClient: BasketClient;
    private _linesClient: LinesClient;
    private _orderClient: OrderClient;
    private _broadcastChannel: BroadcastChannel;

    basket$: Observable<BasketReturnModel>;
    shippingMethods$: Observable<ShippingMethodReturnModel[]>;
    private _newsletterClient: NewsletterClient;
    private _subscriptions: Subscription[];
    private _getResp: any;

    get basket(): BasketReturnModel {
        return this._basket$.getValue();
    }

    constructor(
        @Inject(CATALOG)
        private _catalog: string,
        @Inject(BASE_URL)
        baseUrl: string,
        private translationService: TranslateService,
        private tagManagerService: TagManagerService,
        private _ApplicationRef: ApplicationRef,
        @Inject(NOTIFICATION)
        private notificationService: ReplaySubject<NotificationModel>,
        @Inject(GOOGLE_MAPS_API_KEY)
        private googleMapsKey: string,
    ) {
        if ('BroadcastChannel' in window) {
            this._broadcastChannel = new BroadcastChannel("basket");
            this._broadcastChannel.onmessage = (msg) => {
                // this._basket$.next(JSON.parse(msg.data) as BasketReturnModel)
                // this._ApplicationRef.tick();
            };
        }
        this._basketClient = new BasketClient(baseUrl);
        this._linesClient = new LinesClient(baseUrl);
        this._orderClient = new OrderClient(baseUrl);
        this._newsletterClient = new NewsletterClient(baseUrl);
        this._basket$ = new BehaviorSubject(null);
        this._shippingMethods$ = new BehaviorSubject(null);
        this.basket$ = this._basket$.pipe(shareReplay(1), startWith(this._basket$.getValue()));
        this.shippingMethods$ = this._shippingMethods$.pipe(shareReplay(1), startWith(this._shippingMethods$.getValue()));
        this.get();
        this._subscriptions = new Array<Subscription>();
    }

    ngOnDestroy(): void {
        this._subscriptions.forEach(s => {
            if (s != null && !s.closed)
                s.unsubscribe();
        })
        if ('BroadcastChannel' in window) {
            this._broadcastChannel.close();
        }
    }

    async confirm() {
        let fn = () => this._basketClient.setAccepted(this._catalog);
        let _fn = fn;
        let resp = await PromiseUtils.retryWeb(_fn).catch(errors => {
            this.notificationService.next({
                title: errors.status,
                text: errors.message,
                color: 'var(--error-color, white)',
                backgroundColor: 'var(--error-background-color, red)',
                position: 'top-mid'
            });
            return {
                basket: this.basket,
                shippingMethods: this._shippingMethods$.getValue()
            };
        });
        this._updateBasket(resp);
    }

    getInquiryTypes(): Promise<{ id: string; value: string; name: string; }[]> {
        return PromiseUtils.cache(() => new Promise<{ id: string; value: string; name: string; }[]>(async (resolve, reject) => {
            resolve(Promise
                .all(
                    new Array(5)
                        .fill('INQUIRY.OPTIONS.OPTION')
                        .map((_, i) => this.translationService
                            .get(_ + (i + 1))
                            .toPromise()
                        )
                )
                .then(a => a.map((_, i) => ({
                    id: `${i + 1}`,
                    value: _,
                    name: _,
                })))

            )
        }), 'getInquiryTypes')
    }

    getCountries(): Promise<ICountry[]> {
        return Promise.resolve(countriesMock);
    }

    getCountry(): Promise<ICountry> {
        return this.getCountries().then(countries => {
            let code = this._catalog.toLowerCase().split('-').find((_, i, a) => i == a.length - 1);
            let country = countries.find(country => country.code.toLowerCase() === code);
            return country;
        })
    }

    async getBusinessLocations(): Promise<Array<DeliveryAddressReturnModel>> {

        let fn = async () => this._basketClient.getBusinessAddresses(this._catalog)

        let _fn = fn;
        let resp = await PromiseUtils.retryWeb(_fn);
        return resp;
    }

    async getOrder(orderId: string) {
        let fn = () => this._orderClient.getOrder(orderId, false);

        let _fn = fn;
        let resp = await PromiseUtils.retryWeb(_fn);
        if (isDevMode())
            resp.isTracked = false
        const order = resp.order
        // try {
        //     let ga4Event = MapToGA4.purchase(order);
        //     if (!resp.isTracked)
        //         this._track(ga4Event);
        // } catch (e) {
        //     console.debug(e);
        // }
        order.lines = this.mapOrderLines(order.lines);
        return order;
    }

    async get(): Promise<BasketResponseModel> {
        let resp = this._getResp;
        if (resp == null) {
            let fn = () => this._basketClient.get(this._catalog);
            let _fn = fn;
            resp = await PromiseUtils.retryWeb(_fn);
            this._getResp = resp;
            setTimeout(() => {
                this._getResp = null;
            }, 3000);
        }
        this._updateBasket(resp);
        return resp;
    }

    async deleteBasket(): Promise<void> {
        const oldBasket = this.basket;
        let fn = () => this._basketClient.deleteBasket(this._catalog);
        let _fn = fn;
        let resp = await PromiseUtils.retryWeb(_fn);
        // try {
        //     let ga4Event = MapToGA4.deleteBasket(oldBasket);
        //     this._track(ga4Event);
        // } catch (e) {
        //     console.debug(e);
        // }
        await this._updateBasket(null);
        return resp;
    }

    async updateAddresses(addresses: UpdateBasketAddressModel): Promise<BasketResponseModel> {
        const oldBasket = this.basket;
        let fn = () => this._basketClient.updateAddresses(this._catalog, addresses,);
        let _fn = fn;
        let resp = await PromiseUtils.retryWeb(_fn);
        // try {
        //     let ga4Event = MapToGA4.updateAddresses(resp, addresses, oldBasket);
        //     this._track(ga4Event)
        // } catch (e) {
        //     console.debug(e);
        // }
        await this._updateBasket(resp);
        return resp;
    }

    async updateNote(note?: string): Promise<BasketResponseModel> {
        const oldBasket = this.basket;
        let fn = () => this._basketClient.updateNote(this._catalog, note);
        let _fn = fn;
        let resp = await PromiseUtils.retryWeb(_fn);
        // try {
        //     let ga4Event = MapToGA4.updateNote(resp, note, oldBasket);
        //     this._track(ga4Event)
        // } catch (e) {
        //     console.debug(e);
        // }
        await this._updateBasket(resp);
        return resp;
    }

    async setShipment(shipping?: UpdateShippingModel): Promise<BasketResponseModel> {
        const oldBasket = this.basket;
        let fn = () => this._basketClient.setShipment(this._catalog, shipping,);
        let _fn = fn;
        let resp = await PromiseUtils.retryWeb(_fn);
        // try {
        //     let ga4Event = MapToGA4.setShipment(resp, shipping, oldBasket);
        //     this._track(ga4Event)
        // } catch (e) {
        //     console.debug(e);
        // }
        await this._updateBasket(resp);
        return resp;
    }

    async getShippingMethods(): Promise<ShippingMethodReturnModel[]> {
        let fn = () => this._basketClient.getShippingMethods(this._catalog);
        let _fn = fn;
        let resp = await PromiseUtils.retryWeb(_fn);
        return resp;
    }

    async getPickupPoints(provider: string, postalcode: string, addrline?: string, countryCodeTwoLetter?: string): Promise<PickupPoint[]> {

        let fn = async () => {
            let resp = await this._basketClient.getPickupPoints(this._catalog, provider, postalcode, addrline, countryCodeTwoLetter ?? 'dk', undefined).catch(error => {
                this.notificationService.next({
                    title: error?.status,
                    text: Object.values(error?.errors ?? {}).join('\n'),
                    color: 'var(--error-color, white)',
                    backgroundColor: 'var(--error-background-color, red)',
                    position: 'top-mid'
                });
                return [];
            });

            return resp;
        };
        let _fn = fn;
        let resp = await PromiseUtils.retryWeb(_fn);
        return resp;
    }

    async createLine(itemsModel: AddLineItemModel) {
        const oldBasket = this.basket;
        let fn = () => this._linesClient.createLine(this._catalog, itemsModel,);
        let _fn = fn;
        let resp = await PromiseUtils.retryWeb(_fn);
        // try {
        //     let ga4Event = MapToGA4.updateOrCreateLine(resp, itemsModel, oldBasket);
        //     this._track(ga4Event);
        // } catch (e) {
        //     console.debug(e);
        // }
        resp = await this._updateBasket(resp);
        return resp
    }

    async updateOrCreateLine(itemsModel: AddLineItemModel) {
        const oldBasket = this.basket;
        let fn = () => this._linesClient.updateOrCreateLine(this._catalog, itemsModel,);
        let _fn = fn;
        let resp = await PromiseUtils.retryWeb(_fn);
        // try {
        //     let ga4Event = MapToGA4.updateOrCreateLine(resp, itemsModel, oldBasket);
        //     this._track(ga4Event);
        // } catch (e) {
        //     console.debug(e);
        // }
        await this._updateBasket(resp);
        return resp
    }

    async getPaymentMethods(): Promise<PaymentMethods> {
        let fn = () => this._basketClient.getPaymentMethods(this._catalog)
        let paymentMethods = await PromiseUtils.retryWeb(fn);
        return paymentMethods
            .map(method => ({
                id: method.id,
                images: method.id.toUpperCase().split('/').map(part => `/dist/assets/icons/cards/${part}.svg`),
                name: `PAYMENT_CARD.${method.id.toUpperCase().replaceAll('/', '')}`
            }))
    }

    async setNewsLetter(accept: boolean, source: Origin, firstName?: string, lastName?: string, email?: string): Promise<BasketResponseModel> {
        ///
        firstName = firstName ?? this.basket.billingAddress.firstName,
            lastName = lastName ?? this.basket.billingAddress.lastName,
            email = email ?? this.basket.billingAddress.email;
        if (accept)
            await this._newsletterClient.post(this._catalog, { email, firstName, lastName, source } as NewsletterPostBody)
        return this.get();
    }

    async setAcceptTerms(accept: boolean): Promise<BasketResponseModel> {
        let fn = () => this._basketClient.setAcceptTerms(this._catalog, accept,);
        let _fn = fn;
        let resp = await PromiseUtils.retryWeb(_fn);
        this._updateBasket(resp);
        return resp;
    }

    async purchase(paymentMethodId: string): Promise<string> {
        return new Promise<string>(async (resolve, reject) => {
            const oldBasket = this.basket;
            let fn = () => this._basketClient.getPaymentUrl(this._catalog, paymentMethodId);
            let _fn = fn;
            let paymentUrl = await PromiseUtils.retryWeb(_fn).catch(msg => reject(msg));
            // try {
            //     let ga4Event = MapToGA4.setPayment(oldBasket, paymentMethodId);
            //     this._track(ga4Event);
            // } catch (e) {
            //     console.debug(e);
            // }
            if (!paymentUrl)
                reject(new Error('No Response'));
            resolve(paymentUrl as string);
        });
    }

    async getCityByAddress(address: string): Promise<string> {
        let _url = new URL('https://maps.googleapis.com/maps/api/geocode/json');
        _url.searchParams.append('key', this.googleMapsKey);
        _url.searchParams.append('sensor', 'true');
        _url.searchParams.append('address', address);
        const response = await fetch(_url);
        const msg = await response.json()
        let city: string;
        let country = this._catalog.split('-').find((_, i, a) => i + 1 === a.length);
        let section = msg?.results?.find(r => r.address_components.some(c => c?.short_name?.toLowerCase() === country?.toLowerCase()));
        let component = section?.address_components?.find(component => component?.types?.includes('locality'));
        city = component?.long_name ?? '';
        return city;
    }

    private async _updateBasket(response: BasketResponseModel): Promise<BasketResponseModel> {
        if (response?.basket != null) {
            response.basket.lines = this.mapBasketLines(response.basket.lines);
        }
        this._basket$.next(response?.basket);
        this._shippingMethods$.next(response?.shippingMethods ?? []);
        if (this._broadcastChannel)
            this._broadcastChannel.postMessage(JSON.stringify(response?.basket))
        return response;
    }

    private mapBasketLines(lines: LineItemReturnModel[]): LineItemReturnModel[] {
        return lines.map((line, _, lines) => {
            if (line.parentId != null)
                return;
            const children = lines.filter(_line => _line.parentId === line.id);
            let query = new URLSearchParams();
            [line.sku, ...children.map(c => c.sku)].forEach(sku => {
                let [id, variation] = sku.split(',')
                query.set(id, variation);
            })
            line.productUrl = UrlUtil.safeUrl(query, new URL(line.productUrl, window.origin)).href;
            line.addons = children;
            return line;
        })
            .filter(_ => _);
    }

    private mapOrderLines(lines: LineItemReturnModel[]): LineItemReturnModel[] {
        return lines.map((line, _, lines) => {
            if (line.parentId != null)
                return;
            const children = lines.filter(_line => _line.parentId === line.id);
            let query = new URLSearchParams();
            [line.sku, ...children.map(c => c.sku)].forEach(sku => {
                let [id, variation] = sku.split(',')
                query.set(id, variation);
            })
            line.productUrl = UrlUtil.safeUrl(query, new URL(line.productUrl, window.origin)).href;
            line.addons = children;
            return line;
        })
            .filter(_ => _);
    }

    private _track(arg0: GA4.GA4) {
        this.tagManagerService.postMessage(arg0);
    }
}


